// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY (relocation-plan.md section 17): a relocation never leaves a gap in the open channel. After an own move
// out of it, an Undo or Resume into it, or another device's move (MESSAGE_DELETE_BULK), the loaded span is fetched
// again from the server (not the cache), so every message between the first and the last loaded one is shown, and a
// window left short takes the neighbours that now come into view. One run per channel at a time (one more when asked
// meanwhile); ids removed while a run fetches stay out of what it writes. The refill only adds (section 19, as Obyrus
// v2026.10.119 in section 18): a loaded row the server's pages don't list stays. The list keeps its reading row through
// ScrollManager's anchor, as for any other change of the loaded rows. Obyrus does the same
// (ChatViewModel.refillWindowAfterRelocation). Exclude from upstream sync.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import MessagingMessages from '@app/features/messaging/state/MessagingMessages';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {http} from '@app/features/platform/transport/RestTransport';
import type {Message as WireMessage} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';

/** The server's maximum page. */
const REFILL_PAGE_SIZE = 100;
/** A window left with fewer rows than this takes an older (or newer) page. */
const REFILL_MIN_ROWS = 50;

const running = new Set<string>();
const again = new Set<string>();
const removedDuringRun = new Map<string, Set<string>>();

export interface RelocationLoadedSpan {
	oldest: string;
	newest: string;
	hasMoreBefore: boolean;
	hasMoreAfter: boolean;
}

export interface RelocationRefill {
	/** Every message the server has in the refilled span, plus any page added at its edges. */
	messages: Array<WireMessage>;
	/** An older or newer page came back short: the window now reaches that end of the channel. */
	reachedOldest: boolean;
	reachedNewest: boolean;
	/** At the live edge, a full page came back above the newest loaded row: more messages follow it. */
	moreNewer: boolean;
}

/** Ids removed from a channel (bulk delete) while its refill is fetching: they must not come back from its pages. */
export function noteRelocationRemoval(channelId: string, ids: ReadonlyArray<string>): void {
	const removed = removedDuringRun.get(channelId);
	if (!removed) return;
	for (const id of ids) removed.add(id);
}

const byIdAscending = (a: {id: string}, b: {id: string}) => (BigInt(a.id) < BigInt(b.id) ? -1 : 1);

async function page(
	channelId: string,
	query: {before?: string; after?: string; limit: number},
): Promise<Array<WireMessage>> {
	const response = await http.get<Array<WireMessage>>(Endpoints.CHANNEL_MESSAGES(channelId), {query});
	return Array.isArray(response.body) ? response.body : [];
}

async function refillOnce(channelId: string): Promise<void> {
	const span = MessagingMessages.relocationLoadedSpan(channelId);
	if (!span) return;
	const fetched = new Map<string, WireMessage>();
	let cursor = (BigInt(span.oldest) - 1n).toString();
	for (;;) {
		const rows = await page(channelId, {after: cursor, limit: REFILL_PAGE_SIZE});
		const inSpan = rows.filter((message) => BigInt(message.id) <= BigInt(span.newest));
		for (const message of inSpan) fetched.set(message.id, message);
		if (rows.length < REFILL_PAGE_SIZE || inSpan.length < rows.length) break;
		const newestId = rows.reduce((max, message) => (BigInt(message.id) > BigInt(max) ? message.id : max), cursor);
		if (BigInt(newestId) <= BigInt(cursor)) break;
		cursor = newestId;
	}
	let reachedOldest = false;
	let reachedNewest = false;
	if (fetched.size < REFILL_MIN_ROWS && span.hasMoreBefore) {
		const wanted = REFILL_MIN_ROWS - fetched.size;
		const older = await page(channelId, {before: span.oldest, limit: wanted});
		for (const message of older) fetched.set(message.id, message);
		reachedOldest = older.length < wanted;
	}
	let moreNewer = false;
	if (!span.hasMoreAfter) {
		// At the live edge, a move can bring in messages newer than the newest loaded one, and no MESSAGE_CREATE tells
		// of them: the page above it is always read (relocation-plan.md section 19).
		const newer = await page(channelId, {after: span.newest, limit: REFILL_PAGE_SIZE});
		for (const message of newer) fetched.set(message.id, message);
		moreNewer = newer.length >= REFILL_PAGE_SIZE;
	} else if (fetched.size < REFILL_MIN_ROWS) {
		const wanted = REFILL_MIN_ROWS - fetched.size;
		const newer = await page(channelId, {after: span.newest, limit: wanted});
		for (const message of newer) fetched.set(message.id, message);
		reachedNewest = newer.length < wanted;
	}
	if (SelectedChannel.currentChannelId !== channelId) return;
	const removed = removedDuringRun.get(channelId) ?? new Set<string>();
	MessagingMessages.applyRelocationRefill(channelId, {
		messages: [...fetched.values()].filter((message) => !removed.has(message.id)).sort(byIdAscending),
		reachedOldest,
		reachedNewest,
		moreNewer,
	});
}

/** Fetch the open channel's loaded span again; a channel that is not open is left alone (it reloads on open). */
export async function refillWindowAfterRelocation(channelId: string): Promise<void> {
	if (SelectedChannel.currentChannelId !== channelId) return;
	if (running.has(channelId)) {
		again.add(channelId);
		return;
	}
	running.add(channelId);
	removedDuringRun.set(channelId, new Set<string>());
	try {
		do {
			again.delete(channelId);
			await refillOnce(channelId);
		} while (again.has(channelId) && SelectedChannel.currentChannelId === channelId);
	} catch {
		// A failed refill leaves the window as it was; the next relocation, reload or channel open fills it.
	} finally {
		running.delete(channelId);
		again.delete(channelId);
		removedDuringRun.delete(channelId);
	}
}
